import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { apiErrorMessage } from '../../i18n';
import { api } from '../../lib/api';

type ContentLibraryLightboxTitleProps = {
  organisationId: string;
  asset: MediaAsset;
  onError: (message: string | null) => void;
};

/** Selects the name without its extension, like a desktop file rename. */
function selectStem(input: HTMLInputElement) {
  const dot = input.value.lastIndexOf('.');
  input.setSelectionRange(0, dot > 0 ? dot : input.value.length);
}

export function ContentLibraryLightboxTitle({
  organisationId,
  asset,
  onError,
}: ContentLibraryLightboxTitleProps) {
  const { t } = useTranslation('content');
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  // Unmounting the focused input can fire a late blur after Enter/Escape already settled.
  const settledRef = useRef(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(asset.originalFilename);

  useEffect(() => {
    setEditing(false);
    setDraft(asset.originalFilename);
  }, [asset.id, asset.originalFilename]);

  useEffect(() => {
    if (!editing || !inputRef.current) return;
    settledRef.current = false;
    inputRef.current.focus();
    selectStem(inputRef.current);
  }, [editing]);

  const rename = useMutation({
    mutationFn: (name: string) =>
      api.updateMediaAsset(organisationId, asset.id, { originalFilename: name }),
    onSuccess: async (updated) => {
      onError(null);
      queryClient.setQueryData<MediaAsset[]>(['media', organisationId], (rows) =>
        rows?.map((row) =>
          row.id === updated.id ? { ...row, originalFilename: updated.originalFilename } : row,
        ),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['media', organisationId] }),
        queryClient.invalidateQueries({ queryKey: ['search', organisationId] }),
        queryClient.invalidateQueries({ queryKey: ['playlist-items'] }),
      ]);
    },
    onError: (err) => {
      setDraft(asset.originalFilename);
      onError(apiErrorMessage(err, t('lightbox.renameError')));
    },
  });

  const commit = () => {
    if (settledRef.current) return;
    settledRef.current = true;
    const name = draft.trim();
    setEditing(false);
    if (!name || name === asset.originalFilename) {
      setDraft(asset.originalFilename);
      return;
    }
    setDraft(name);
    rename.mutate(name);
  };

  const cancel = () => {
    settledRef.current = true;
    setDraft(asset.originalFilename);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        maxLength={300}
        aria-label={t('lightbox.renameLabel')}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            cancel();
          }
        }}
        className="w-full max-w-[520px] rounded-lg border border-[#E4763A] bg-white/10 px-2.5 py-1 text-[17px] font-extrabold text-white outline-none"
      />
    );
  }

  const shownName = rename.isPending ? draft : asset.originalFilename;

  return (
    <button
      type="button"
      title={t('lightbox.rename')}
      onClick={() => setEditing(true)}
      className="group flex min-w-0 max-w-full items-center gap-2 rounded-lg px-2.5 py-1 -ms-2.5 text-start transition hover:bg-white/10"
    >
      <span className="truncate text-[17px] font-extrabold text-white">{shownName}</span>
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-[#A3A29D] transition group-hover:text-white"
        aria-hidden
      >
        <path d="M12 20h9" />
        <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
      </svg>
    </button>
  );
}
